import { AsyncActionCreatorTypeValue } from '../actionCreators/types';
import { AsyncActionMethods } from '../types/ActionMethods';
import { createAsyncRedux } from '../redux/lib';
import { HttpVerb } from '@sonar-software/sonar-http';

import {
	AsyncSlice,
	AsyncSliceOptions,
	DeleteAsyncSlice,
	DeleteAsyncSliceOptions,
	GetAsyncSlice,
	GetAsyncSliceOptions,
	PatchAsyncSlice,
	PatchAsyncSliceOptions,
	PostAsyncSlice,
	PostAsyncSliceOptions,
	PutAsyncSlice,
	PutAsyncSliceOptions
} from './types';
import { createHttpSaga, HttpSagaOptions } from '../../http';
import { createWatcherSaga, SagaYieldType, WatcherSagaYieldStep } from '../../lib/redux';

export function createAsyncSlice<HV extends HttpVerb, N extends string, RQ = void, RC = void>(
	options: AsyncSliceOptions<HV, N, RQ, RC>
): AsyncSlice<HV, N, RQ, RC> {
	const { asyncActionCreators, asyncActionCreatorsMap, asyncActionTypesMap, asyncReducers, asyncState } =
		createAsyncRedux<HV, N, RQ, RC>(options, options.defaultRequest);

	const { httpSagaOptions = {} } = options;

	const httpOptions: HttpSagaOptions<RQ, RC> = {
		allowUnauthorized: httpSagaOptions.allowUnauthorized,
		cache: httpSagaOptions.cache,
		errorCodeActionCreators: [ ...httpSagaOptions.errorCodeActionCreators ?? [], asyncActionCreatorsMap.error ],
		failCodeActionCreators: [ ...httpSagaOptions.failCodeActionCreators ?? [], asyncActionCreatorsMap.failed ],
		preRequestActionCreators: [ ...httpSagaOptions.preRequestActionCreators ?? [] ],
		requestHandler: options.requestHandler,
		statusCodeHandlers: [ ...httpSagaOptions.statusCodeHandlers ?? [] ],
		successActionCreators: [ ...httpSagaOptions.successActionCreators ?? [] ],
		successCombinationActionCreators: [ ...httpSagaOptions.successCombinationActionCreators ?? [] ],
		successDataActionCreators: [ ...httpSagaOptions.successDataActionCreators ?? [] ],
		successDataHandlers: [ ...httpSagaOptions.successDataHandlers ?? [] ]
	};

	if (options.receive) {
		httpOptions.successDataActionCreators = [
			...httpSagaOptions.successDataActionCreators ?? [],
			asyncActionCreatorsMap.receive
		];
	} else {
		httpOptions.successActionCreators = [
			...httpSagaOptions.successActionCreators ?? [],
			asyncActionCreatorsMap.receive as AsyncActionCreatorTypeValue<AsyncActionMethods.Receive, HV, N>
		];
	}

	const httpSaga = createHttpSaga(httpOptions);

	const watcherSteps = [
		{
			action: asyncActionTypesMap.request,
			actionHandler: httpSaga,
			type: options.yieldType ?? SagaYieldType.Latest
		},
		...options.additionalReceiveSteps?.map((step) => ({
			...step,
			action: asyncActionTypesMap.receive
		})) ?? [],
		...options.additionalRequestSteps?.map((step) => ({
			...step,
			action: asyncActionTypesMap.request
		})) ?? []
	];

	const watcherSaga = createWatcherSaga(watcherSteps as WatcherSagaYieldStep[]);

	return {
		asyncActionCreators,
		asyncReducers,
		asyncState,
		asyncWatchers: watcherSaga
	};
}

export function createDeleteAsyncSlice<N extends string, RQ = void, RC = void>(
	options: DeleteAsyncSliceOptions<N, RQ, RC>
): DeleteAsyncSlice<N, RQ, RC> {
	return createAsyncSlice<HttpVerb.Delete, N, RQ, RC>({
		...options,
		httpVerb: HttpVerb.Delete
	});
}

export function createGetAsyncSlice<N extends string, RQ = void, RC = void>(
	options: GetAsyncSliceOptions<N, RQ, RC>
): GetAsyncSlice<N, RQ, RC> {
	return createAsyncSlice<HttpVerb.Get, N, RQ, RC>({
		...options,
		httpVerb: HttpVerb.Get
	});
}

export function createPatchAsyncSlice<N extends string, RQ = void, RC = void>(
	options: PatchAsyncSliceOptions<N, RQ, RC>
): PatchAsyncSlice<N, RQ, RC> {
	return createAsyncSlice<HttpVerb.Patch, N, RQ, RC>({
		...options,
		httpVerb: HttpVerb.Patch
	});
}

export function createPostAsyncSlice<N extends string, RQ = void, RC = void>(
	options: PostAsyncSliceOptions<N, RQ, RC>
): PostAsyncSlice<N, RQ, RC> {
	return createAsyncSlice<HttpVerb.Post, N, RQ, RC>({
		...options,
		httpVerb: HttpVerb.Post
	});
}

export function createPutAsyncSlice<N extends string, RQ = void, RC = void>(
	options: PutAsyncSliceOptions<N, RQ, RC>
): PutAsyncSlice<N, RQ, RC> {
	return createAsyncSlice<HttpVerb.Put, N, RQ, RC>({
		...options,
		httpVerb: HttpVerb.Put
	});
}
